import { useState } from 'react';
import { failureOf, said } from '@/api/errors';
import { useRemoveTask, useRemoveThread, type RemovalCategory, type RemovalResult } from '@/api/hooks';
import { Dialog } from '@/ui/Dialog';
import { Select, Textarea } from '@/ui/fields';
import { Field, Switch } from '@/ui/primitives';
import { useToast } from '@/ui/Toast';

/**
 * Removing something from the work, with why (ADR-0158). A task is cancelled
 * and its reason kept; a conversation on the day is closed and its reason
 * kept. The reason is what the platform learns from: once the same source
 * keeps being removed, the people who can stop it are offered a rule or a
 * direction, and nothing changes until they say so.
 */

export const REMOVAL_WORDS: ReadonlyArray<{ value: RemovalCategory; label: string }> = [
  { value: 'noise', label: 'Not work: noise, a notification, an advert' },
  { value: 'not_needed', label: 'No longer needed' },
  { value: 'not_mine', label: 'Not mine, or not this department’s' },
  { value: 'wrong_source', label: 'Should never have been made from where it came' },
  { value: 'duplicate', label: 'A duplicate of something else' },
  { value: 'done_elsewhere', label: 'Done somewhere else' },
  { value: 'other', label: 'Something else' },
];

export function RemoveDialog({
  target,
  title,
  onClose,
}: {
  target: { taskId: string; twin?: boolean } | { threadId: string; domain?: string | null };
  title: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const removeTask = useRemoveTask();
  const removeThread = useRemoveThread();
  const [category, setCategory] = useState<RemovalCategory>('noise');
  const [reason, setReason] = useState('');
  const [notWork, setNotWork] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const thread = 'threadId' in target;
  const pending = removeTask.isPending || removeThread.isPending;
  const done = (r: RemovalResult) => {
    toast({
      text: r.ruleId
        ? `Removed. Mail from ${r.senderDomain} is not work from now on; change it in your mail rules.`
        : r.proposed
          ? `Removed. ${r.countedAgainst ? `You keep removing ${r.countedAgainst}` : 'This keeps happening'}: you are offered a way to stop it.`
          : 'Removed, with your reason. It is off the day.',
    });
    onClose();
  };
  const failed = (e: unknown) => setError(said(failureOf(e)));
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Remove"
      title={thread ? 'Take this off the day?' : 'Remove this task?'}
      secondary={{ label: 'Keep it', onClick: onClose }}
      primary={{
        label: 'Remove',
        disabled: pending || reason.trim().length < 3,
        onClick: () => {
          setError(null);
          if ('taskId' in target) removeTask.mutate({ taskId: target.taskId, category, reason: reason.trim() }, { onSuccess: done, onError: failed });
          else removeThread.mutate({ threadId: target.threadId, category, reason: reason.trim(), ...(notWork ? { notWork: true } : {}) }, { onSuccess: done, onError: failed });
        },
      }}
      note={thread ? 'The conversation is closed, never deleted: it stays in the mail and can be read.' : 'The task is cancelled, never deleted: what was said about it stays.'}
    >
      <p className="m-0 text-body">“{title}”</p>
      <Field label="Why it goes">
        <Select aria-label="Why it goes" value={category} onChange={(e) => setCategory(e.target.value as RemovalCategory)} options={REMOVAL_WORDS} />
      </Field>
      <Field
        label="In your words"
        hint={
          'twin' in target && target.twin
            ? 'Kept with the removal, for you alone. Nothing is learned from your twin’s tasks and nobody else is told.'
            : 'Kept with the removal, and what the platform learns from. Only this department reads it; for your own mail, only you.'
        }
      >
        <Textarea aria-label="In your words" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={1000} placeholder="GitHub notifications are not work for this team" />
      </Field>
      {'threadId' in target && target.domain && (
        <Switch checked={notWork} onChange={setNotWork} label={`Mail from ${target.domain} is not work from now on`} />
      )}
      {error && <p className="m-0 text-dense text-danger">{error}</p>}
    </Dialog>
  );
}
